// What a serf is DOING, as one glanceable glyph floated overhead. Shared by
// the 3D HUD overlay and the 2.5D sprite pass so both views tell one story.
// null = draw nothing (animals, soldiers' plain walks, states with no story).
// Owner: client-render.

import { isTerraform } from '../../shared/constants'
import { isArmed } from '../../shared/person'
import type { UnitWire } from '../../shared/types'
import type { GameState } from '../state'
import { sitePrepPhase } from './terrain'

/** Icons for foreign serfs degrade gracefully: their job/errand fields are
 *  stripped from the wire, so they fall back to plain state glyphs. */
export function statusIcon(st: GameState, u: UnitWire): string | null {
  if (u.type !== 'citizen' || !u.person) return null
  // Loot remains a visible objective on the return journey, even while the
  // raider pauses. Combat/fleeing still get their usual activity in the badge.
  if (u.raidLoot && u.hp > 0 && Object.values(u.carrying ?? {}).some(n => (n ?? 0) > 0) &&
    u.state !== 'fighting' && u.state !== 'fleeing') return '🏴‍☠️'
  const job = u.job !== undefined ? st.buildings.get(u.job) : undefined
  switch (u.state) {
    case 'fighting':
      return '⚔️'
    case 'fleeing':
      return '💨'
    case 'feeding':
      return '🍞'
    case 'hauling':
    case 'waitingStorage':
      return '📦'
    case 'errand':
      return '🎒' // armory trip: swapping the held item
    case 'building':
      if (!job) return '🔨'
      if (job.type === 'chop') return '🪓'
      if (job.type === 'plant') return '🌱'
      if (isTerraform(job.type)) return '🪏'
      if (job.done) return '🔨' // repair work
      switch (sitePrepPhase(st, job)) {
        case 'clear': return '🪓'
        case 'level': return '🪏'
        default: return '🔨'
      }
    case 'toJob':
    case 'working': {
      if (u.craftHaul) return '📦' // crafter hauling material from the annex to the bench
      if (!job) return u.state === 'working' ? '🛠️' : null
      if (job.type === 'chop') return '🪓'
      if (job.type === 'plant') return '🌱'
      if (isTerraform(job.type)) return '🪏'
      if (!job.done) {
        switch (sitePrepPhase(st, job)) {
          case 'clear': return '🪓'
          case 'level': return '🪏'
          default: return '🔨'
        }
      }
      switch (job.type) {
        case 'farm': return '🌾'
        case 'pier': return '🎣'
        case 'quarry': return '⛏️'
        case 'mine': return '⛏️'
        case 'workbench': return '🪚'
        case 'townhall': return '🪚' // whittling a hall order
        case 'barracks': return '⚒️'
        case 'dummy': return '🎯'
        default: return '🛠️'
      }
    }
    case 'moving':
      if (u.exploring) return '🧭'
      if (u.scav) return '🧺'
      if (u.carrying && Object.values(u.carrying).some((v) => (v ?? 0) > 0)) return '📦'
      return null
    case 'idle':
      if (u.exploring) return '🧭'
      // A standing guard is on duty, not slacking; a bare/tooled serf with
      // nothing to do wears its idleness openly (drawn fainter by callers).
      return u.person && isArmed(u.person) ? '🛡️' : '💤'
    default:
      return null
  }
}

/** Idle reads quieter than work — callers dim the 💤 so a resting village
 *  doesn't shout. */
export function statusAlpha(icon: string): number {
  return icon === '💤' ? 0.55 : 0.92
}
